import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Phone, Mail, MessageSquare, Calendar as CalendarIcon, Clock, User } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

export default function CalendarPage() {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setCurrentUser(user);
    fetchFollowups();
  }, [currentDate]);

  const fetchFollowups = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // Get first and last day of current month
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1).toISOString().split("T")[0];
      const lastDay = new Date(year, month + 1, 0).toISOString().split("T")[0];

      const response = await axios.get(
        `${API}/calendar/followups?start_date=${firstDay}&end_date=${lastDay}`,
        { headers }
      );
      setFollowups(response.data);
    } catch (error) {
      toast.error("Failed to load calendar data");
    } finally {
      setLoading(false);
    }
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();
    
    const days = [];
    
    // Add empty slots for days before the first day
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add actual days
    for (let i = 1; i <= totalDays; i++) {
      const date = new Date(year, month, i);
      const dateStr = date.toISOString().split("T")[0];
      days.push({ day: i, date: dateStr });
    }
    
    return days;
  }, [currentDate]);

  // Group followups by date
  const followupsByDate = useMemo(() => {
    const grouped = {};
    followups.forEach(followup => {
      const date = followup.scheduled_date?.split("T")[0];
      if (date) {
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(followup);
      }
    });
    return grouped;
  }, [followups]);

  const goToPrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    setSelectedDate(null);
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    setSelectedDate(null);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split("T")[0]);
  };

  const getFollowupIcon = (channel) => {
    switch (channel) {
      case "phone": return <Phone className="h-4 w-4" />;
      case "email": return <Mail className="h-4 w-4" />;
      case "whatsapp": return <MessageSquare className="h-4 w-4" />;
      default: return <CalendarIcon className="h-4 w-4" />;
    }
  };

  const getFollowupColor = (channel) => {
    switch (channel) {
      case "phone": return "bg-green-100 text-green-800 border-green-200";
      case "email": return "bg-blue-100 text-blue-800 border-blue-200";
      case "whatsapp": return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "sms": return "bg-purple-100 text-purple-800 border-purple-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const today = new Date().toISOString().split("T")[0];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="fade-in">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-4xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                Follow-up Calendar
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                View and manage scheduled follow-ups
              </p>
            </div>
            <Button
              onClick={goToToday}
              variant="outline"
              className="rounded-sm"
              data-testid="today-btn"
            >
              Today
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-md border border-slate-200 shadow-sm">
              {/* Calendar Header */}
              <div className="flex items-center justify-between p-6 border-b border-slate-200">
                <button
                  onClick={goToPrevMonth}
                  className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="h-5 w-5 text-slate-600" />
                </button>
                <h2 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <button
                  onClick={goToNextMonth}
                  className="p-2 hover:bg-slate-100 rounded-sm transition-colors"
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="h-5 w-5 text-slate-600" />
                </button>
              </div>

              {/* Day Headers */}
              <div className="grid grid-cols-7 border-b border-slate-200">
                {DAYS.map(day => (
                  <div
                    key={day}
                    className="p-3 text-center text-sm font-medium text-slate-600 uppercase tracking-wider"
                  >
                    {day}
                  </div>
                ))}
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7">
                {calendarDays.map((dayObj, index) => {
                  const dayFollowups = dayObj.date ? followupsByDate[dayObj.date] || [] : [];
                  const isToday = dayObj.date === today;
                  const isSelected = dayObj.date === selectedDate;

                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] border-b border-r border-slate-100 p-2 transition-colors cursor-pointer ${
                        dayObj.day ? "hover:bg-slate-50" : "bg-slate-50/50"
                      } ${isSelected ? "bg-blue-50" : ""}`}
                      onClick={() => dayObj.date && setSelectedDate(dayObj.date)}
                      data-testid={dayObj.date ? `calendar-day-${dayObj.date}` : undefined}
                    >
                      {dayObj.day && (
                        <>
                          <div className="flex items-center justify-between mb-1">
                            <span
                              className={`text-sm font-medium ${
                                isToday
                                  ? "bg-blue-900 text-white w-7 h-7 rounded-full flex items-center justify-center"
                                  : "text-slate-700"
                              }`}
                            >
                              {dayObj.day}
                            </span>
                            {dayFollowups.length > 0 && (
                              <span className="text-xs bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full font-medium">
                                {dayFollowups.length}
                              </span>
                            )}
                          </div>
                          <div className="space-y-1">
                            {dayFollowups.slice(0, 2).map((followup, i) => (
                              <div
                                key={followup.id || i}
                                className={`text-xs px-1.5 py-1 rounded border truncate ${getFollowupColor(followup.channel)}`}
                              >
                                {followup.lead_name || "Follow-up"}
                              </div>
                            ))}
                            {dayFollowups.length > 2 && (
                              <div className="text-xs text-slate-500 text-center">
                                +{dayFollowups.length - 2} more
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar - Selected Day Details */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-md border border-slate-200 shadow-sm sticky top-4">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {selectedDate
                    ? new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                      })
                    : "Select a Date"}
                </h3>
              </div>
              <div className="p-6">
                {selectedDate ? (
                  followupsByDate[selectedDate]?.length > 0 ? (
                    <div className="space-y-4">
                      {followupsByDate[selectedDate].map((followup, index) => (
                        <div
                          key={followup.id || index}
                          className="p-4 bg-slate-50 rounded-lg border border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                          onClick={() => followup.lead_id && navigate(`/leads/${followup.lead_id}`)}
                          data-testid={`followup-detail-${followup.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={`p-1.5 rounded ${getFollowupColor(followup.channel)}`}>
                                {getFollowupIcon(followup.channel)}
                              </div>
                              <span className="text-xs uppercase font-medium text-slate-600">
                                {followup.channel}
                              </span>
                            </div>
                            {followup.status && (
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                followup.status === "completed" ? "bg-green-100 text-green-800" :
                                followup.status === "pending" ? "bg-yellow-100 text-yellow-800" :
                                "bg-slate-100 text-slate-800"
                              }`}>
                                {followup.status}
                              </span>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 mb-2">
                            <User className="h-4 w-4 text-slate-400" />
                            <span className="font-semibold text-slate-900">
                              {followup.lead_name || "Unknown Lead"}
                            </span>
                          </div>
                          
                          {followup.lead_phone && (
                            <p className="text-sm text-slate-600 mb-2">
                              {followup.lead_phone}
                            </p>
                          )}
                          
                          {followup.notes && (
                            <p className="text-sm text-slate-600 bg-white p-2 rounded border border-slate-200">
                              {followup.notes}
                            </p>
                          )}
                          
                          <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                            <Clock className="h-3 w-3" />
                            <span>Scheduled for {followup.scheduled_date?.split("T")[0]}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No follow-ups scheduled</p>
                      <p className="text-sm text-slate-400 mt-1">
                        Select a lead to add a follow-up
                      </p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-8">
                    <CalendarIcon className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">Click on a date to see details</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upcoming Follow-ups Summary */}
            <div className="bg-white rounded-md border border-slate-200 shadow-sm mt-6">
              <div className="p-6 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                  This Month&apos;s Summary
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-3xl font-bold text-blue-900">{followups.length}</p>
                    <p className="text-sm text-blue-700">Total Follow-ups</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-3xl font-bold text-green-900">
                      {followups.filter(f => f.status === "completed").length}
                    </p>
                    <p className="text-sm text-green-700">Completed</p>
                  </div>
                  <div className="text-center p-4 bg-yellow-50 rounded-lg">
                    <p className="text-3xl font-bold text-yellow-900">
                      {followups.filter(f => f.status === "pending").length}
                    </p>
                    <p className="text-sm text-yellow-700">Pending</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-lg">
                    <p className="text-3xl font-bold text-slate-900">
                      {Object.keys(followupsByDate).length}
                    </p>
                    <p className="text-sm text-slate-700">Active Days</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
